import React, { memo, useCallback, useMemo, useState } from 'react'
import { UseFormMethods, useWatch } from 'react-hook-form'
import {
  ControlledCurrencyInput,
  ControlledHiddenInput,
  IControlledCurrencyInputProps,
} from 'transfix-form/components'
import { FormIf } from 'transfix-form/components/FormIf'
import AddIcon from 'transfix-icons/build/AddIcon'
import RefreshIcon from 'transfix-icons/build/RefreshIcon'
import Button from 'transfix-ui/components/Button'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { Maybe } from 'types/graphqlTypes'
import { CounterOfferCardOverflowMenu } from 'apps/marketplace/components/CarrierOptionRateCard/components/CounterOfferCard/components'
import { formatRate } from 'utils/formatRate'
import { getErrorMessage } from '../../schema'
import { TEST_IDS } from './constants'

const useCounterOfferFormSectionStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    backgroundColor: brandColors.skyBlue1,
    padding: theme.spacing(1.5),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}))

export interface ICounterOfferFormSectionProps
  extends Pick<IControlledCurrencyInputProps, 'name'>,
    Pick<UseFormMethods, 'control' | 'setValue' | 'errors'> {}

const CounterOfferFormSection = memo<ICounterOfferFormSectionProps>(
  ({ control, errors, name, setValue }) => {
    const classes = useCounterOfferFormSectionStyles()
    const [isEditing, setIsEditing] = useState<boolean>(false)

    const counterOfferAmount = useWatch<Maybe<number>>({ control, name })

    const createClickHandler = useCallback(() => {
      setIsEditing(true)
    }, [])

    const editClickHandler = useCallback(() => {
      setIsEditing(true)
    }, [])

    const deleteClickHandler = useCallback(() => {
      setValue(name, null)
    }, [name, setValue])

    const showCounterOffer = useCallback(
      (amount: Maybe<number>) => {
        return Boolean(amount) || isEditing
      },
      [isEditing]
    )

    const title = useMemo(() => {
      if (isEditing) {
        return 'Counter Offer'
      }
      return `Counter Offer: ${formatRate(counterOfferAmount)}`
    }, [counterOfferAmount, isEditing])

    return (
      <>
        <ControlledHiddenInput control={control} name={name} />
        <FormIf
          control={control}
          name={name}
          when={showCounterOffer}
          otherwise={
            <Button
              data-test={TEST_IDS.counterOfferAddButton}
              color='primary'
              startIcon={<AddIcon color='skyBlue6' size='large' />}
              variant='text'
              onClick={createClickHandler}
              noPadding>
              Add Counter Offer
            </Button>
          }>
          <div className={classes.card} data-test={TEST_IDS.counterOfferCard}>
            <div className={classes.header}>
              <div className={classes.title}>
                <RefreshIcon size='large' color='skyBlue6' />
                <Typography variant='subtitle2'>{title}</Typography>
              </div>
              <div>
                <CounterOfferCardOverflowMenu
                  disabled={isEditing}
                  onEditClick={editClickHandler}
                  onDeleteClick={deleteClickHandler}
                />
              </div>
            </div>
            {isEditing && (
              <ControlledCurrencyInput
                control={control}
                name={name}
                CurrencyInputProps={{
                  large: true,
                  dataTest: TEST_IDS.counterOfferInput,
                  invalid: !!getErrorMessage(errors, name),
                  validationMessage: getErrorMessage(errors, name),
                }}
              />
            )}
          </div>
        </FormIf>
      </>
    )
  }
)

export default CounterOfferFormSection
